// dependencies
import { format, isAfter, isValid, parseISO } from "date-fns";
import React from "react";

import Avatar from "../../../../../../components/Avatar";
//components
import Button from "../../../../../../components/Button";
import Link from "../../../../../../components/Link";
import { ScrollableColumn } from "../../../../../../components/TableLite/TableLiteScrollable/TableLiteScrollable";
import TruncatedText from "../../../../../../components/Text/TruncatedText";

//utils
import get from "../../../../../../utils/GetNested";
import toEmployeeUrl from "../../../../../../utils/URLs/Employee";
import { arrayIncludes, findAuthorOrSigner } from "../../../utils";

//css
import css from "../../../UserReviews.module.scss";

export const FormsINeedToDoTableCols = (userId: string): ScrollableColumn[] => {
	return [
		{
			id: "view",
			label: "",
			width: 96,
			isSortable: false,
			getter: (report) => (
				<div>
					<Button href={`/review/${report._id}/`} color="blue">
						Open Form
					</Button>
				</div>
			),
		},
		{
			id: "subject",
			label: "Subject",
			width: 150,
			isSortable: false,
			getter: (report) => {
				const subjectID = get(report, ["subject"], null);
				const subjectName = get(report, ["subject_name"], null);

				return (
					<div className={css.userBox}>
						<Avatar size={20} className={css.userAvatar} id={subjectID} />
						<div className={css.userName}>
							<Link href={toEmployeeUrl(subjectID)} forceHardReload={true}>
								{subjectName}
							</Link>
						</div>
					</div>
				);
			},
		},
		{
			id: "form",
			label: "Form",
			isSortable: false,
			width: 380,
			getter: (report) => {
				const formName = get(report, ["form_name"], null);
				return <TruncatedText text={formName} maxWidth={376} size="paragraph-3" weight="regular" />;
			},
		},
		{
			id: "completeDate",
			label: "Complete Date",
			sortName: "complete_date",
			width: 150,
			isSortable: true,
			getter: (report) => {
				const date = get(report, ["complete_date"], null);
				// the maxkey is result in complete date reports
				// to be returned first so these should render null
				if (get(report, ["completed"]) === false) {
					return null;
				} else if (date) {
					const parsedDate = parseISO(date);

					return isValid(parsedDate) ? format(parsedDate, "MMM d, yyyy hh:mm a") : null;
				}

				return null;
			},
		},
		{
			id: "status",
			width: 240,
			label: "Status",
			isSortable: false,
			getter: (report) => {
				const submit = get(report, ["submit"], false);
				const startDate = get(report, ["start_date"], null);
				const completed = get(report, ["completed"], false);
				const redacted = get(report, ["redacted"], false);
				const authorTurn = get(report, ["author_turn"], 0);
				const signerTurn = get(report, ["signer_turn"], 0);
				const authors = get(report, ["author_objects"], []);
				const authorIDs = get(report, ["authors"], []);
				const signers = get(report, ["signer_objects"], []);
				const pastAuthors = get(report, ["past_authors"], null);
				const pastSigners = get(report, ["past_signers"], null);
				// Below are the current author and signer but to prevent conflict of variable names
				const currentSigner = get(report, ["signer"], null);
				const currentAuthor = get(report, ["author"], null);
				let status = "";

				if (completed) {
					return <TruncatedText text={"Completed"} maxWidth={236} size="paragraph-3" weight="regular" />;
				}

				const parsedDate = parseISO(startDate);
				if (startDate && isAfter(parsedDate, new Date())) {
					status = `Starting ${format(parsedDate, "MMM d, yyyy h:mm a")}`;
					return <TruncatedText text={status} maxWidth={236} size="paragraph-3" weight="regular" />;
				}

				if (
					!submit &&
					(authorTurn < authorIDs?.length ||
						arrayIncludes(
							authors.map((author) => author._id),
							"_redacted",
							0
						))
				) {
					// check if author signed but not submitted yet
					if (arrayIncludes(pastAuthors, userId, 0)) {
						const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
						status = currentAuthorInfo?.name
							? `Awaiting submission by ${currentAuthorInfo.name} (Filled out the form)`
							: "Awaiting submission by Unassigned (Filled out the form)";
					}

					// For users Forms I Need To Do
					else if (
						arrayIncludes(authorIDs, userId, authorTurn) ||
						arrayIncludes(
							authors.map((author) => author._id),
							"_redacted",
							0
						)
					) {
						if (currentAuthor !== userId) {
							const currentAuthorInfo = findAuthorOrSigner(
								get(report, ["author_objects"]),
								currentAuthor
							);
							status = `Awaiting submission by ${
								redacted ? "Anonymous" : currentAuthorInfo?.name || "Unassigned"
							} (Fill out the form)`;
						} else {
							status = "Fill out the form";
						}
					} else {
						const currentAuthorInfo = findAuthorOrSigner(get(report, ["author_objects"]), currentAuthor);
						status = `Awaiting submission by ${
							redacted ? "Anonymous" : currentAuthorInfo.name || "Unassigned"
						} (Sign off the form)`;
					}
				} else if (signerTurn <= signers?.length) {
					if (currentSigner === userId) {
						// if the user is the current signer and the were an author and all of teh authors have authored the form
						status = "Sign off the form (Filled out the form)";
					} else if (arrayIncludes(pastAuthors, userId, 0)) {
						const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
						status = currentSignerInfo?.name
							? `Awaiting sign off by ${currentSignerInfo.name} (Filled out the form)`
							: "Awaiting sign off by Unassigned (Filled out the form)";
					} else if (arrayIncludes(pastSigners, userId, 0)) {
						const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
						status = `Awaiting sign off by ${
							currentSignerInfo?.name || "Unassigned"
						} (Signed off the form)`;
					} else if (currentSigner !== userId) {
						const currentSignerInfo = findAuthorOrSigner(get(report, ["signer_objects"]), currentSigner);
						status = `Awaiting sign off by ${currentSignerInfo?.name || "Unassigned"}`;
					} else {
						status = "Sign off the form";
					}
				}

				return <TruncatedText text={status} maxWidth={236} size="paragraph-3" weight="regular" />;
			},
		},
	];
};

export default FormsINeedToDoTableCols;
